import { createContext, useCallback, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"

/**
 * 밝게/어둡게. 창고에서 밤에 쓰는 사람이 있고, 낮에도 눈이 편한 쪽이 사람마다 다르다.
 *
 * 고른 값은 이 브라우저에만 남는다(계정 설정이 아니다) — 같은 계정을 창고 PC 와
 * 사무실 PC 에서 쓰는데 한쪽에서 바꾸면 다른 쪽까지 바뀌는 것이 오히려 이상하다.
 * 고르지 않았으면 OS 설정을 따라가고, OS 설정이 바뀌면 바로 따라 바뀐다.
 *
 * 색 값은 전부 `styles/tokens.css` 의 `[data-theme="dark"]` 에 있다. 여기는 그 이름만 붙인다.
 */
export type Theme = "light" | "dark"

const STORAGE_KEY = "returns.theme"

function systemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function storedChoice(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === "light" || value === "dark" ? value : null
  } catch {
    // 사생활 보호 모드에서는 읽기 자체가 막힌다. 그때는 OS 설정을 따른다.
    return null
  }
}

type ThemeValue = { theme: Theme; toggle: () => void }

const ThemeContext = createContext<ThemeValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<Theme | null>(() => storedChoice())
  const [system, setSystem] = useState<Theme>(() => systemTheme())

  // 고르지 않았을 때만 OS 를 따라간다.
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)")
    if (!media) return
    const onChange = () => setSystem(media.matches ? "dark" : "light")
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [])

  const theme = choice ?? system

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  const toggle = useCallback(() => {
    setChoice((current) => {
      const next: Theme = (current ?? systemTheme()) === "dark" ? "light" : "dark"
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // 저장이 막혀도 이번 세션 동안은 바뀐 채로 쓴다.
      }
      return next
    })
  }, [])

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error("ThemeProvider 안에서만 쓸 수 있습니다")
  return value
}
